import {
  getAllMentorshipRounds,
  getMyMentorshipPartners,
  getMyMentorshipRegistration,
  postMyMentorshipRegistration,
  getMyMentorshipMatchResult,
  getMyMentorshipMeetingLog,
  getMyMentorshipMeetingsV2,
} from "@/api/mentorshipApi";

import {
  calculateMentorshipSlots,
  calculateRoundStatus,
} from "@/pages/PersonalDashboard/utils/mentorshipRounds";
import { MentorshipParticipantRoles } from "@/constants/MentorshipParticipantRoles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { FEATURE_FLAGS } from "@/constants/FeatureFlags";

/**
 * React hook for loading and managing mentorship-related data
 * for the Personal Dashboard.
 *
 * Responsibilities:
 * - Fetch all mentorship rounds and determine current registration
 *   and feedback slots.
 * - Load the current user's registration data for the active round.
 * - Lazily load past mentorship partners.
 * - Provide helpers for saving registration data.
 *
 * This hook encapsulates business rules around:
 * - which round is currently actionable
 * - whether registration or feedback actions are enabled
 *
 * @returns {{
 *   regRoundId: string | null,
 *   feedbackRoundId: string | null,
 *   isRegistrationOpen: boolean,
 *   isFeedbackEnabled: boolean,
 *   registration: Object | null,
 *   saveRegistration: (data: Object) => Promise<any> | undefined,
 *   isLoading: boolean,
 *   isPartnersLoading: boolean,
 *   loadPastPartners: () => Promise<void>,
 *   userTimezone: string | null
 * }}
 */
export const useMentorshipData = () => {
  const flags = useFeatureFlags();
  const useV2Meetings = !!flags[FEATURE_FLAGS.CREATE_GOOGLE_MEETING];

  const [roundStatus, setRoundStatus] = useState({
    regRoundId: null,
    feedbackRoundId: null,
    isFeedbackEnabled: false,
    matchResultRoundName: "",
    canViewMatch: false,
  });
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  // Current user's registration data for the active round
  const [registration, setRegistration] = useState(null);

  // Loading state for initial mentorship data
  const [isLoading, setIsLoading] = useState(true);

  // Cached list of past mentorship partners
  const [pastPartners, setPastPartners] = useState([]);

  // Loading state for user profile timezone
  const [userTimezone, setUserTimezone] = useState(null);

  // Loading state for partners data
  const [isPartnersLoading, setIsPartnersLoading] = useState(false);

  // Round Selector
  const [roundSelectionData, setRoundSelectionData] = useState({
    sortedRounds: [],
    activeRoundId: null,
  });
  const [participantDetails, setParticipantDetails] = useState({
    roundInfo: null,
    partnerMeetingOverview: [],
    participantRole: null,
  });
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  // Loading state for meeting log
  const [isMeetingsLoading, setIsMeetingsLoading] = useState(false);
  // Cache for partners data per round, reset on page mount
  const partnersCacheRef = useRef({});
  // Monotonic id per refreshMeetings call. A response only writes state when its
  // id is still the latest, so a slow earlier round can't clobber a newer one
  // (e.g. fast round switching).
  const requestSeqRef = useRef(0);
  // Guards against state updates after the component unmounts.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * refreshRegistration
   *
   * Refreshes the current user's registration data if the registration round ID (`regRoundId`) is available.
   * It fetches the registration data associated with the current round and updates the `registration` state.
   * If the `regRoundId` is not set, no API call is made.
   *
   * @returns {Promise<void>} - A promise that resolves when the registration data has been refreshed or an error occurs.
   */
  const refreshRegistration = async () => {
    if (!roundStatus.regRoundId) return;
    try {
      const { data: regData } = await getMyMentorshipRegistration(
        roundStatus.regRoundId,
      );
      setRegistration(regData);
    } catch (err) {
      console.error("Failed to refresh registration", err);
    }
  };

  /**
   * useEffect to initialize and load mentorship data.
   *
   * This effect is run once when the component is mounted. It fetches the available mentorship rounds, calculates
   * the mentorship slot status, and then fetches the user's registration data for the active round (if any).
   * The loading state (`isLoading`) is set to `false` once the data has been fetched (or an error has occurred).
   *
   * @returns {void}
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date().toISOString();
        const { data: rounds } = await getAllMentorshipRounds();

        const status = calculateMentorshipSlots(rounds);
        setRoundStatus(status);

        // If there is an actionable round, fetch the user's registration data
        // Note: registration data is fetched based on the registration slot first
        if (status.regRoundId) {
          const { data: regData } = await getMyMentorshipRegistration(
            status.regRoundId,
          );
          setRegistration(regData);

          const participantRole = regData?.roundPreferences?.participantRole;
          const deadlineKey =
            participantRole === MentorshipParticipantRoles.MENTOR
              ? "mentorApplicationDeadlineAt"
              : "menteeApplicationDeadlineAt";
          const regRound = rounds.find(
            (r) => r.id?.toString() === status.regRoundId?.toString(),
          );
          setIsRegistrationOpen(now < regRound?.timeline?.[deadlineKey]);

          if (regData && regData.isRegistered) {
            try {
              const { data: matchData } = await getMyMentorshipMatchResult(
                status.regRoundId,
              );
              setMatchResult(matchData);
            } catch (matchErr) {
              console.error("Failed to fetch match result", matchErr);
            }
          } else {
            setMatchResult(null);
          }
        }

        const selectionData = calculateRoundStatus(rounds);
        setRoundSelectionData(selectionData);

        if (selectionData.activeRoundId) {
          setSelectedRoundId(selectionData.activeRoundId);
          setIsMeetingsLoading(true);
        }
      } catch (err) {
        console.error("Failed to fetch mentorship data", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  /**
   * Lazily load the user's past mentorship partners.
   *
   * @returns {Promise<void>}
   */
  const loadPastPartners = async () => {
    setIsPartnersLoading(true);
    try {
      const { data: allPastPartners } = await getMyMentorshipPartners();
      setPastPartners(allPastPartners || []);
    } catch (err) {
      console.error("Failed to fetch partners data", err);
    } finally {
      setIsPartnersLoading(false);
    }
  };

  /**
   * Save or update the user's mentorship registration.
   *
   * This operation is only allowed when:
   * - a valid registration round exists
   * - registration for that round is currently open
   *
   * @param {Object} data - Registration payload submitted by the user.
   * @returns {Promise<any> | undefined} API response when saved, or undefined if not allowed.
   */
  const saveRegistration = async (data) => {
    // Only allow saving when registration is open and a valid round exists
    if (!roundStatus.regRoundId || !isRegistrationOpen) return;
    return postMyMentorshipRegistration(roundStatus.regRoundId, data);
  };

  const handleRoundChange = useCallback(
    (id) => {
      if (id !== selectedRoundId) {
        setParticipantDetails({
          roundInfo: null,
          partnerMeetingOverview: [],
          participantRole: null,
        });
        setIsMeetingsLoading(true);
      }
      setSelectedRoundId(id);
    },
    [selectedRoundId],
  );

  const refreshMeetings = useCallback(async () => {
    if (!selectedRoundId) return;
    const seq = ++requestSeqRef.current;
    setIsMeetingsLoading(true);

    try {
      const [{ data: meetingLog }, { data: partnersInfo }] = await Promise.all([
        useV2Meetings
          ? getMyMentorshipMeetingsV2({
              roundId: selectedRoundId,
              includeDetails: false,
            })
          : getMyMentorshipMeetingLog(selectedRoundId),
        partnersCacheRef.current[selectedRoundId]
          ? Promise.resolve({ data: partnersCacheRef.current[selectedRoundId] })
          : getMyMentorshipPartners(selectedRoundId),
      ]);

      // A newer round was selected while this request was in flight — drop the
      // stale response so it can't overwrite the current round's data.
      if (!isMountedRef.current || seq !== requestSeqRef.current) return;

      partnersCacheRef.current[selectedRoundId] ??= partnersInfo;
      setUserTimezone((prev) => prev ?? meetingLog?.userTimezone ?? null);

      const currentRound = roundSelectionData.sortedRounds.find(
        (r) => r.id.toString() === selectedRoundId.toString(),
      );

      if (!partnersInfo || partnersInfo.length === 0) {
        const isRegisteredForRound =
          selectedRoundId?.toString() === roundStatus.regRoundId?.toString() &&
          registration?.isRegistered === true;
        setParticipantDetails({
          roundInfo: currentRound,
          partnerMeetingOverview: [],
          participantRole: null,
          isRegistered: isRegisteredForRound,
        });
        return;
      }

      const requiredMeetings = currentRound?.requiredMeetings ?? 0;
      const partnerMeeting = (partnersInfo || []).map((partner) => {
        const info = meetingLog?.meetingInfo?.find(
          (i) => i.partnerId.toString() === partner.id.toString(),
        );
        const completedCount = info?.completedMeetingsCount ?? 0;
        const completedRate =
          requiredMeetings > 0
            ? Math.round((completedCount / requiredMeetings) * 100)
            : 0;

        return {
          partnerId: partner.id,
          preferredName: partner.preferredName,
          requiredMeetings,
          completedCount,
          completedRate,
          meetingTimeList: info?.meetingTimeList || [],
          participantRole: info?.participantRole,
        };
      });

      const globalParticipantRole =
        partnerMeeting?.[0]?.participantRole ?? null;

      setParticipantDetails({
        roundInfo: currentRound,
        partnerMeetingOverview: partnerMeeting,
        participantRole: globalParticipantRole,
      });
    } catch (MeetingErr) {
      console.error("Failed to fetch meeting log", MeetingErr);
    } finally {
      // Only the latest in-flight request controls the loading flag.
      if (isMountedRef.current && seq === requestSeqRef.current) {
        setIsMeetingsLoading(false);
      }
    }
  }, [
    selectedRoundId,
    roundSelectionData.sortedRounds,
    roundStatus.regRoundId,
    registration?.isRegistered,
  ]);

  useEffect(() => {
    if (!selectedRoundId) {
      setParticipantDetails({
        roundInfo: null,
        partnerMeetingOverview: [],
        participantRole: null,
      });
      return;
    }
    refreshMeetings();
  }, [selectedRoundId, refreshMeetings]);

  return {
    ...roundStatus,
    isRegistrationOpen,
    // registration
    registration,
    saveRegistration,
    refreshRegistration,
    // loading states
    isLoading,
    isParticipantCardLoading: isLoading || isMeetingsLoading,
    isPartnersLoading,
    // partner history
    loadPastPartners,
    pastPartners,
    // match result
    matchResult,
    // round selector for participant card
    selectedRoundId,
    roundSelectionData,
    handleRoundChange,
    // meeting
    refreshMeetings,
    participantDetails,
    // user profile timezone
    userTimezone,
  };
};
